import Component from 'vue-class-component'
Component.registerHooks(['beforeRouteEnter', 'beforeRouteLeave'])

import Vue from 'vue'
import router from './router'
import store from './store'
import Config from './config/vue.config'
import APIRequestLoader from './utils/APIRequestLoader'
import TitleMixin from './utils/Title'
import Resources from '@/utils/Resources'
import { EventTracker } from '@/utils/event_tracker'
import { CustomTraceProvider } from '@/utils/custom_trace'
import { PerformanceLogger } from '@/utils/performance_logger'
import i18n from './i18n'
import { sendLog } from '@/utils/log'
import { isProduction } from '@/utils/env'
import App from './NoticePageApp.vue'
import { initSelfXssWarning } from '@/utils/selfXssWarning'

initSelfXssWarning()
EventTracker.default.setup()

Vue.config.productionTip = false
Vue.prototype.$config = Config
Vue.prototype.$resources = new Resources()
Vue.prototype.$loader = new APIRequestLoader()
Vue.mixin(TitleMixin)

const sendError = async (message: string) => {
  // 一旦マイページ関係に限る
  if (!location.pathname.startsWith('/mypage')) {
    return
  }
  await sendLog(`mypage_error. ${message}`, 'error')
}

Vue.config.errorHandler = (err: Error, vm: Vue, info: string) => {
  // eslint-disable-next-line
  if (!isProduction()) console.error('vue error', err, info)
  sendError(`vue error. err:${err} info:${info}`)
}
window.addEventListener('error', (event: ErrorEvent) => {
  // eslint-disable-next-line
  if (!isProduction()) console.error('uncaught error', event)
  sendError(
    `uncaught error. err:${event.error.message} stack:${event.error.stack}`
  )
})
window.addEventListener(
  'unhandledrejection',
  (event: PromiseRejectionEvent) => {
    // eslint-disable-next-line
    if (!isProduction()) console.error('unhandled promise rejection', event)
    sendError(`unhandled promise rejection. reason:${event.reason}`)
  }
)

// ページごとにDOMContentLoadedからloadまでの区間をトレースする
const loadEventTraceNamePrefix = 'CUSTOM_TRACE:LOAD:'

const getLoadEventTraceName = (pathname: string): string => {
  // /viewer/xxx または /profile/xxx の場合は集約をする
  if (!pathname.startsWith('/viewer/') && !pathname.startsWith('/profile/')) {
    return loadEventTraceNamePrefix + pathname
  }
  const segments = pathname.split('/')
  return loadEventTraceNamePrefix + segments.slice(0, 2).join('/') + '/*'
}

window.addEventListener('DOMContentLoaded', () => {
  CustomTraceProvider.startTrace(getLoadEventTraceName(location.pathname))
})
window.addEventListener('load', () => {
  CustomTraceProvider.stopTrace(getLoadEventTraceName(location.pathname))
})

// PerformanceObserverのLCP/FCPをトレース及びログに出力する
// https://developer.mozilla.org/en-US/docs/Web/API/PerformanceObserver
const performanceLogger = new PerformanceLogger()
const observer = new PerformanceObserver((list) => {
  if (!performanceLogger.isLogTarget(location.pathname)) {
    return
  }
  const now = Date.now()
  const pathname = location.pathname
  for (const entry of list.getEntries()) {
    if (entry.entryType === 'largest-contentful-paint') {
      performanceLogger.logLargestContentfulPaint(entry, now, pathname)
    } else if (entry.entryType === 'paint') {
      if (entry.name === 'first-contentful-paint') {
        performanceLogger.logFirstContentfulPaint(entry, now, pathname)
      }
    }
  }
})
observer.observe({ type: 'largest-contentful-paint', buffered: true }) // Safari未対応でPCとAndroidのみ
observer.observe({ type: 'paint', buffered: true })

new Vue({
  router: router,
  store,
  i18n,
  render: (h) => h(App),
}).$mount('#app')
